/**
 * Persisted master/music/sfx volume + mute, shared by `/fly`'s settings popover and the title
 * screen's music (`Home.tsx` reads the same stored value for `TITLE_MUSIC_PATH`'s volume, per
 * `docs/engineering/audio-plan.md`'s "modern default" volume sliders).
 *
 * Kept storage-agnostic (`SettingsStore` is just `getItem`/`setItem`) so the round-trip logic is
 * unit-testable with a plain in-memory fake — `bun test` has no DOM, so there is no real
 * `localStorage` global to test against (same reasoning as `assets/viewers/AudioViewer.tsx`'s
 * `hasAudioContext()` feature-detect for `AudioContext`).
 */

export interface AudioSettings {
  /** 0..1, applied on top of the per-bus volumes below. */
  readonly master: number;
  readonly music: number;
  readonly sfx: number;
  readonly muted: boolean;
}

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  master: 0.8,
  music: 0.7,
  sfx: 0.9,
  muted: false,
};

export const AUDIO_SETTINGS_STORAGE_KEY = "red-opus/audio-settings";

export interface SettingsStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function clampVolume(value: unknown): number {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? Math.min(1, Math.max(0, num)) : 0;
}

/** Reads whatever survives a `JSON.parse` of untrusted stored text into a well-formed settings object, falling back per-field to the defaults. */
export function normalizeAudioSettings(candidate: unknown): AudioSettings {
  if (typeof candidate !== "object" || candidate === null) return DEFAULT_AUDIO_SETTINGS;
  const raw = candidate as Partial<Record<keyof AudioSettings, unknown>>;
  return {
    master: raw.master === undefined ? DEFAULT_AUDIO_SETTINGS.master : clampVolume(raw.master),
    music: raw.music === undefined ? DEFAULT_AUDIO_SETTINGS.music : clampVolume(raw.music),
    sfx: raw.sfx === undefined ? DEFAULT_AUDIO_SETTINGS.sfx : clampVolume(raw.sfx),
    muted: raw.muted === true,
  };
}

export function serializeAudioSettings(settings: AudioSettings): string {
  return JSON.stringify(settings);
}

/** Defaults on missing key, corrupt JSON, or a non-object payload — never throws. */
export function loadAudioSettings(store: SettingsStore): AudioSettings {
  const raw = store.getItem(AUDIO_SETTINGS_STORAGE_KEY);
  if (raw === null) return DEFAULT_AUDIO_SETTINGS;
  try {
    return normalizeAudioSettings(JSON.parse(raw));
  } catch {
    return DEFAULT_AUDIO_SETTINGS;
  }
}

export function saveAudioSettings(store: SettingsStore, settings: AudioSettings): void {
  store.setItem(AUDIO_SETTINGS_STORAGE_KEY, serializeAudioSettings(settings));
}

/** The volume a bus should actually play at: 0 when muted, otherwise `master * bus`. */
export function effectiveBusVolume(settings: AudioSettings, bus: number): number {
  return settings.muted ? 0 : clampVolume(settings.master) * clampVolume(bus);
}

/** `window.localStorage` when one exists (browser), `undefined` in a headless/test context. */
export function browserSettingsStore(): SettingsStore | undefined {
  if (typeof window === "undefined" || !("localStorage" in window)) return undefined;
  try {
    return window.localStorage;
  } catch {
    // Some browsers throw on `localStorage` access itself (privacy mode) rather than just being absent.
    return undefined;
  }
}
